import _Typography from "@mui/material/Typography";
import _LocationOn from "@mui/icons-material/LocationOn";
import _MyLocation from "@mui/icons-material/MyLocation";
import _Explore from "@mui/icons-material/Explore";
import _Autocomplete from "@mui/material/Autocomplete";
import _Grid from "@mui/material/Grid";
import _TextField from "@mui/material/TextField";
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import debounce from 'lodash/debounce';
import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { BikeRoute, Place, Ride, UserPlace, placeTypeIcons, userPlaceTypes } from '../../models';
import { GeocoderService } from '../../services';
export function Autocomplete({
  center: mapCenter,
  nbResults,
  id,
  margin,
  size,
  required,
  disabled,
  disableFloatingLabel,
  disableClearable,
  customOptions,
  hideCustomOptionsOnSearch,
  defaultValue,
  error,
  inputProps,
  isManualInputInvalid,
  label: _label,
  onInputChange,
  onSelect,
  onCustomOptionSelect,
  ...props
}) {
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [invalidInput, setInvalidInput] = useState(false);
  const fetch = useMemo(() => debounce((request, callback, center) => {
    GeocoderService.autocomplete(request, {
      center,
      nbResults
    }, callback);
  }, 700), []);
  const {
    t
  } = useTranslation();
  const inputId = id || 'places-autocomplete';
  useEffect(() => {
    if (defaultValue === null) setInputValue('');
    setValue(defaultValue || null);
    setOptions(defaultValue ? [defaultValue] : []);
  }, [defaultValue]);
  useEffect(() => {
    let active = true;
    if (!inputValue || value && !value.addressDetail || value && value.strPoint === inputValue || value && value.address === inputValue) return undefined;
    fetch(inputValue, places => {
      if (active) {
        let newOptions = [];
        if (value) newOptions = [value];
        if (places) newOptions = [...newOptions, ...places];
        setOptions(newOptions);
        setLoading(false);
      }
    }, mapCenter);
    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);
  function getKey(place) {
    const {
      id
    } = place;
    if (!id) return '';
    const isGeolocation = id === 'geolocation';
    if (place instanceof Ride) return `ride-${id}`;
    if (place instanceof BikeRoute) return `bike-route-${id}`;
    if (isGeolocation) return `geolocation`;
    if (place instanceof UserPlace) return `user-place-${id}`;
    if (place instanceof Place) return `place-${id}`;
    return '';
  }
  useEffect(() => {
    const node = document.getElementById(inputId);
    const typedBeforeHydration = node?.value;
    if (!typedBeforeHydration || defaultValue) return;
    setInputValue(typedBeforeHydration);
    onInputChange?.(typedBeforeHydration);
  }, []);
  const label = _label || t('commons.actions.search_address');
  return /*#__PURE__*/React.createElement(_Autocomplete, _extends({
    autoComplete: true,
    filterSelectedOptions: true,
    includeInputInList: true,
    clearOnBlur: false,
    clearText: t('commons.actions.remove') || '',
    disableClearable: disableClearable,
    disabled: disabled,
    filterOptions: x => x,
    forcePopupIcon: Boolean(inputValue && !value),
    getOptionKey: option => option.id || -1,
    getOptionLabel: option => typeof option === 'string' ? option : option instanceof Ride || option instanceof BikeRoute ? option.title || '' : option.address,
    handleHomeEndKeys: false,
    id: inputId,
    inputValue: inputValue,
    isOptionEqualToValue: (option, value) => {
      if (option instanceof UserPlace && value instanceof UserPlace && option.id === value.id) return true;
      if (option instanceof Place && value instanceof Place && option.id === value.id) return true;
      if (option instanceof Ride && value instanceof Ride && option.id === value.id) return true;
      if (option instanceof BikeRoute && value instanceof BikeRoute && option.id === value.id) return true;
      return false;
    },
    loading: Boolean(inputValue) && !value && loading,
    loadingText: /*#__PURE__*/React.createElement(Trans, {
      i18nKey: "commons.loading"
    }),
    noOptionsText: inputValue && /*#__PURE__*/React.createElement(Trans, {
      i18nKey: "commons.no_result_found"
    }),
    onBlur: () => {
      if (isManualInputInvalid && inputValue !== '' && value === null) {
        setInvalidInput(true);
      }
    },
    onChange: (event, newValue) => {
      if (newValue instanceof Ride || newValue instanceof BikeRoute) {
        setOptions([]);
        setInputValue('');
        setValue(null);
        onInputChange?.('');
        onCustomOptionSelect?.(newValue);
        return;
      }
      setOptions(newValue ? [newValue, ...options] : options);
      setValue(newValue);
      onSelect?.(newValue);
    },
    onFocus: () => {
      if (isManualInputInvalid && invalidInput) {
        setInvalidInput(false);
      }
    },
    onInputChange: (event, newInputValue) => {
      setLoading(true);
      setOptions([]);
      setInputValue(newInputValue);
      onInputChange?.(newInputValue);
    },
    options: [...((!hideCustomOptionsOnSearch || !inputValue) && customOptions?.filter(option => {
      if (!inputValue) return true;
      if (option instanceof Ride || option instanceof BikeRoute || option instanceof UserPlace) return option.title && option.title.toLowerCase().indexOf(inputValue.toLowerCase()) > -1 || false;
      return option.address && option.address.toLowerCase().indexOf(inputValue.toLowerCase()) > -1 || false;
    }) || []), ...options],
    renderInput: params => /*#__PURE__*/React.createElement(_TextField, _extends({
      autoComplete: "off",
      label: disableFloatingLabel ? undefined : label,
      placeholder: disableFloatingLabel ? label || '' : undefined,
      required: required,
      variant: "outlined"
    }, inputProps, params, {
      fullWidth: true,
      error: error || invalidInput,
      margin: margin || 'dense',
      slotProps: {
        input: {
          ...inputProps?.slotProps?.input,
          ...params.InputProps
        },
        inputLabel: {
          ...inputProps?.slotProps?.inputLabel,
          ...params.InputLabelProps
        }
      }
    })),
    renderOption: (props, place) => /*#__PURE__*/React.createElement(_Grid, _extends({
      container: true,
      alignItems: "center"
      /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    }, props, {
      key: getKey(place) || props.key,
      sx: {
        width: '100%'
      }
    }), /*#__PURE__*/React.createElement(Item, {
      place: place
    })),
    size: size,
    slotProps: {
      paper: {
        sx: !inputValue || value ? {
          '& .MuiAutocomplete-noOptions': {
            display: 'none'
          }
        } : {}
      }
    },
    style: {
      width: '100%'
    },
    value: value
  }, props));
}
export function Item({
  place
}) {
  const {
    t
  } = useTranslation();
  const isGeolocation = place.id === 'geolocation';
  const Icon = place instanceof Ride || place instanceof BikeRoute ? _Explore : isGeolocation ? _MyLocation : place instanceof UserPlace ? userPlaceTypes[place.type].Icon : null;
  const PlaceTypeIcon = !Icon && 'placeType' in place && place.placeType && placeTypeIcons[place.placeType] || null;
  let title;
  let subtitle;
  if (place instanceof Ride) {
    const {
      title: _title,
      from,
      to
    } = place;
    title = _title || '';
    if (from?.addressDetail) {
      subtitle = from.address;
      if (to?.addressDetail && to.address.localeCompare(from.address) !== 0) {
        subtitle += ` < > ${to.address}`;
      }
    }
  } else if (place instanceof BikeRoute) {
    title = place.title;
  } else {
    title = place.addressDetail?.primaryText || '';
    subtitle = place.addressDetail?.secondaryText;
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(_Grid, {
    alignItems: "center",
    display: "flex",
    style: {
      width: '40px'
    }
  }, Icon ? /*#__PURE__*/React.createElement(Icon, {
    color: "primary"
  }) : PlaceTypeIcon ? /*#__PURE__*/React.createElement(PlaceTypeIcon, {
    color: "action"
  }) : /*#__PURE__*/React.createElement(_LocationOn, {
    color: "action"
  })), /*#__PURE__*/React.createElement(_Grid, {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    size: "grow",
    style: {
      minHeight: '36px',
      width: 'calc(100% - 40px)'
    }
  }, isGeolocation ? /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.my_location"
  }) : place instanceof UserPlace ? /*#__PURE__*/React.createElement(React.Fragment, null, place.title || userPlaceTypes[place.type].titleKey && t(userPlaceTypes[place.type].titleKey || '') || '', place.addressDetail?.primaryText && /*#__PURE__*/React.createElement(_Typography, {
    color: "textSecondary",
    variant: "body2"
  }, place.addressDetail.primaryText)) : /*#__PURE__*/React.createElement(React.Fragment, null, title, subtitle && /*#__PURE__*/React.createElement(_Typography, {
    color: "textSecondary",
    variant: "body2"
  }, subtitle))));
}